/**
 * Which of the record page's tabs its desktop row draws and which it lists under More (#14753),
 * from the room each takes. Pure, so the rule is pinned without a browser.
 */

/** A tab's key and the room it takes in the strip. */
export interface TabRoom {
  key: string;
  room: number;
}

/**
 * Which tabs the strip has no room for (#14753). Every tab fitting beside the action, none, and no
 * room is kept for More. Else the drawn tabs are the longest run from the first that fits with
 * More, the action and the active tab (drawn after the run when it comes later, so it is never
 * under More); the rest are overflowed, in row order. The run stops at the first tab that does not
 * fit, even when a later, narrower one would: the row reads in order and More holds its tail.
 */
export const overflowedTabKeys = (
  tabs: readonly TabRoom[],
  active: string,
  available: number,
  moreRoom: number,
  actionRoom: number
): string[] => {
  // Half a pixel for the rounding of fractional boxes.
  const room = available + 0.5;
  const total = tabs.reduce((sum, tab) => sum + tab.room, 0);
  if (total + actionRoom <= room) return [];
  const activeRoom = tabs.find((tab) => tab.key === active)?.room ?? 0;
  let used = moreRoom + actionRoom + activeRoom;
  const drawn = new Set<string>([active]);
  for (const tab of tabs) {
    if (tab.key === active) continue;
    if (used + tab.room > room) break;
    used += tab.room;
    drawn.add(tab.key);
  }
  return tabs.filter((tab) => !drawn.has(tab.key)).map((tab) => tab.key);
};
